/* ───────────────────────────────────────────────────────────────────────────
   O16 Mobile "Notify me" bridge — owner round-13 (2026-07-17) bug fix.
   NEW, small, defensive script. Does NOT reimplement Klaviyo's Back-in-Stock
   (BIS) flow — it only detects Klaviyo's OWN injected button and forwards a
   click to it.

   ROOT CAUSE (same family as the round-9/10 duplicate-DOM bugs): the theme
   renders the entire product section TWICE —
   `.main-product-desktop-section-wrapper` (first in DOM) +
   `.main-product-mobile-section-wrapper` (one is display:none per
   breakpoint via CSS). On a sold-out variant, the Klaviyo BIS app scans the
   page and injects its own "Notify me when available" button into the
   FIRST matching buy-buttons area it finds — the desktop copy — and
   replaces/augments that copy's disabled "Sold Out" submit. On mobile the
   desktop wrapper is display:none, so the shopper never sees Klaviyo's
   button; the visible mobile copy (and the sticky bar, which is that same
   mobile `.product__buy_buttons` element pinned by
   assets/o16-sticky-buybar.js) keeps showing the theme's plain disabled
   "Sold Out" button.

   FIX: detect Klaviyo's real button wherever it lands (Klaviyo injects
   asynchronously, so a MutationObserver watches for it), and when the
   MOBILE copy is currently sold out, reveal a visually-matching "Notify me"
   button INSIDE the mobile `.product__buy_buttons` that — on click —
   programmatically triggers a click on Klaviyo's REAL button. Because the
   sticky bar docks this same mobile element (not a clone), the bridge
   button rides along into the docked bar automatically — no separate
   sticky-specific wiring needed.

   INERT BY DESIGN when Klaviyo hasn't injected anything (e.g. the DEV
   preview domain, where Klaviyo's onsite JS doesn't run): findKlaviyoButton()
   simply returns null forever, update() never creates a bridge button, and
   the theme's normal Sold Out / Add to cart button is never touched. Every
   DOM read/write is wrapped in try/catch so a detection failure can never
   break add-to-cart or leave the page in a broken visual state.

   Loaded on the PDP the same way the other o16-*.js PDP scripts are
   (sections/main-product.liquid, deferred).
─────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var BRIDGE_CLASS = 'o16-notify-bridge';
  var FALLBACK_LABEL = 'Notify me when available';

  /* A Klaviyo BIS button: a clickable element whose visible text contains
     "notify me" (case-insensitive) AND whose className contains either
     "kl-private-reset-css" or "klaviyo" — matches the live DOM shape
     confirmed in the owner's dump (button.needsclick...kl-private-reset-css-...). */
  function isKlaviyoNotifyButton(el) {
    if (!el || (el.tagName !== 'BUTTON' && el.tagName !== 'A')) return false;
    var text = (el.textContent || '').trim().toLowerCase();
    if (text.indexOf('notify me') === -1) return false;
    var cls = el.className;
    cls = (cls && cls.toString ? cls.toString() : String(cls || '')).toLowerCase();
    return cls.indexOf('kl-private-reset-css') !== -1 || cls.indexOf('klaviyo') !== -1;
  }

  function findKlaviyoButton() {
    try {
      // Klaviyo lands in the buy-buttons area (per the owner's DOM dump:
      // desktop wrapper's .product__buy_buttons > product-form >
      // .product-form__buttons-row) — scope there first, falling back to
      // the whole product section, then the whole page, so this keeps
      // working even if Klaviyo's exact injection point ever shifts.
      var scopes = [
        document.querySelector('.main-product-desktop-section-wrapper .product__buy_buttons'),
        document.querySelector('[id^="MainProduct-"]'),
        document.body
      ];
      for (var s = 0; s < scopes.length; s++) {
        var scope = scopes[s];
        if (!scope) continue;
        var candidates = scope.querySelectorAll('button, a');
        for (var i = 0; i < candidates.length; i++) {
          if (isKlaviyoNotifyButton(candidates[i])) return candidates[i];
        }
      }
    } catch (e) {
      /* Detection must never throw into the caller. */
    }
    return null;
  }

  function getMobileBuyButtons() {
    try {
      return document.querySelector('.main-product-mobile-section-wrapper .product__buy_buttons');
    } catch (e) {
      return null;
    }
  }

  /* Belt-and-suspenders visibility toggle: an inline style always wins the
     cascade regardless of whatever `display` the shared .product-form__submit
     / .button / .button--tertiary base classes might set, so this can never
     repeat the [hidden]-gets-silently-overridden class of bug fixed in
     earlier rounds (assets/o16-cart-drawer.css, assets/o16-product-gallery.css). */
  function show(el) {
    if (el) el.style.display = '';
  }
  function hide(el) {
    if (el) el.style.display = 'none';
  }

  function ensureBridgeButton(mobileButtons) {
    var row = mobileButtons.querySelector('.product-form__buttons-row');
    if (!row) return null;
    var bridge = row.querySelector('.' + BRIDGE_CLASS);
    if (bridge) return bridge;

    bridge = document.createElement('button');
    bridge.type = 'button'; // never a submit — this must never post the add-to-cart form
    bridge.className = BRIDGE_CLASS + ' product-form__submit button button--tertiary focus-inset';

    var label = document.createElement('span');
    label.textContent = FALLBACK_LABEL;
    bridge.appendChild(label);

    bridge.addEventListener('click', function (event) {
      // Not preventDefault()-ing anything Klaviyo needs — this button is
      // type="button" (never submits a form), so there is nothing of ours
      // to prevent; we only forward the click to Klaviyo's real button.
      event.preventDefault();
      try {
        var klaviyoBtn = findKlaviyoButton();
        if (klaviyoBtn) klaviyoBtn.click();
      } catch (e) {
        /* Klaviyo unavailable at click time — nothing else to do safely. */
      }
    });

    var submit = mobileButtons.querySelector('.product-form__submit:not(.' + BRIDGE_CLASS + ')');
    if (submit && submit.parentNode === row) {
      row.insertBefore(bridge, submit.nextSibling);
    } else {
      row.appendChild(bridge);
    }
    hide(bridge); // starts hidden; update() reveals it only when actually needed
    return bridge;
  }

  function update() {
    try {
      var mobileButtons = getMobileBuyButtons();
      if (!mobileButtons) return; // no mobile/desktop wrapper split on this page — nothing to bridge

      var submit = mobileButtons.querySelector('.product-form__submit:not(.' + BRIDGE_CLASS + ')');
      if (!submit) return;

      var isSoldOut = submit.disabled === true || submit.hasAttribute('disabled');
      var klaviyoBtn = isSoldOut ? findKlaviyoButton() : null;

      if (isSoldOut && klaviyoBtn) {
        var bridge = ensureBridgeButton(mobileButtons);
        if (!bridge) return;

        // Keep the label in sync with Klaviyo's own button text when possible.
        var freshLabel = (klaviyoBtn.textContent || '').trim() || FALLBACK_LABEL;
        if (bridge.textContent !== freshLabel) bridge.textContent = freshLabel;

        show(bridge);
        hide(submit);
      } else {
        // In stock, or Klaviyo hasn't injected (yet, or ever — e.g. DEV
        // preview) — stay completely out of the way. The theme's normal
        // Sold Out / Add to cart button is the only thing shown.
        var existingBridge = mobileButtons.querySelector('.' + BRIDGE_CLASS);
        if (existingBridge) hide(existingBridge);
        show(submit);
      }
    } catch (e) {
      /* Defensive: never let a failure here leave the mobile submit hidden
         with no bridge shown — the catch means we simply skip this pass;
         the theme's own disabled "Sold Out" button (never touched unless
         the try block above successfully found a live Klaviyo button) stays
         visible as the safe fallback. */
    }
  }

  var scheduled = false;
  function scheduleUpdate() {
    if (scheduled) return;
    scheduled = true;
    // Debounce — Klaviyo's own injection AND our bridge-button insertion
    // both fire mutations; coalescing avoids re-entrant thrash from the
    // MutationObserver below reacting to its own side effects.
    setTimeout(function () {
      scheduled = false;
      update();
    }, 150);
  }

  function init() {
    // Initial pass — covers a variant that's already sold out at page load
    // (Klaviyo may already have injected before this script even runs).
    scheduleUpdate();

    // Klaviyo injects its button ASYNCHRONOUSLY — watch for it appearing.
    // Scoped to the product section (not the whole document) to cut down
    // on unrelated mutation noise (cart drawer, sticky-bar spacer, etc.).
    try {
      var target = document.querySelector('[id^="MainProduct-"]') || document.body;
      var observer = new MutationObserver(scheduleUpdate);
      observer.observe(target, { childList: true, subtree: true });
    } catch (e) {
      /* MutationObserver unsupported/unavailable — the initial pass above
         still covers the common "already sold out at load" case. */
    }

    // Sold-out state is PER VARIANT — re-check on every selection change.
    document.addEventListener('variant:change', scheduleUpdate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
